import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import StudentRegisterForm from "../../forms/studentRegisterForm";
import StudentTurnStep from "../../forms/studentTurnStep";
import StudentParentSearch from "./studentParentSearch";
import ParentRegisterForm from "../../forms/parentRegisterForm";
import CustomButton from "../../../../components/lib/customButton";
import { toast } from "react-toastify";
import { formatCPF } from "../../../../utils/DocumentUtils";
import { formatCurrency } from "../../../../utils/CurrencyUtils";
import Stepper from "../../../../components/lib/stepperHeader";

const STEP = {
    STUDENT: 0,
    TURNS: 1,
    PARENT_SELECT: 2,
    PARENT_CREATE: 3,
    SUMMARY: 4,
};

const visualSteps = [
    { key: STEP.STUDENT, label: "Aluno" },
    { key: STEP.TURNS, label: "Turma e Turnos" },
    { key: STEP.PARENT_SELECT, label: "Parentesco" },
    { key: STEP.SUMMARY, label: "Resumo" },
];

const MAX_PARENTS = 2;

const Spinner = () => (
    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
);

const StudentRegisterStepper = ({ onFinish, onCancel, onDirty, isSubmitting }) => {
    const [step, setStep] = useState(STEP.STUDENT);

    const [studentDraft, setStudentDraft] = useState({
        name: "",
        email: "",
        phone_number: "",
        grade: "",
        emergency_value: 0,
        spend_limit: 0,
        isPayingStudent: true,
    });

    const [turnsDraft, setTurnsDraft] = useState({ grade: "", turnIds: [], allTurns: [] });

    const [parents, setParents] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);

    const getVisualStep = () => {
        if (step === STEP.PARENT_CREATE) return STEP.PARENT_SELECT;
        return step;
    };

    const submitActiveForm = () => {
        document.querySelector("form[data-step-form]")?.requestSubmit();
    };

    const goBack = () => {
        if (step === STEP.STUDENT) return onCancel();
        if (step === STEP.PARENT_CREATE) return setStep(STEP.PARENT_SELECT);
        if (step === STEP.SUMMARY) return setStep(STEP.PARENT_SELECT);
        setStep(step - 1);
    };

    const isDuplicateParent = (incomingParent) => {
        return parents.some(({ data }) => {
            if (data.id && incomingParent.id) return data.id === incomingParent.id;
            if (data.cpf && incomingParent.cpf) return data.cpf === incomingParent.cpf;
            if (data.email && incomingParent.email) return data.email === incomingParent.email;
            return false;
        });
    };

    const addParent = (parent, source) => {
        if (parents.length >= MAX_PARENTS) {
            toast.warning("Máximo de 2 responsáveis permitido.");
            return;
        }
        if (isDuplicateParent(parent)) {
            toast.error("Responsável já adicionado.");
            return;
        }
        setParents([...parents, { data: parent, source }]);
        setStep(STEP.SUMMARY);
    };

    const removeParent = (index) => {
        setParents(parents.filter((_, i) => i !== index));
    };

    const canAddMoreParents = parents.length < MAX_PARENTS;

    return (
        <div className="flex flex-col gap-6">
            <Stepper steps={visualSteps} currentStep={getVisualStep()} />

            {/* CONTENT */}
            <div className="min-h-[360px]">
                <AnimatePresence mode="wait">
                    <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>

                        {/* STEP 1 — Dados do aluno */}
                        {step === STEP.STUDENT && (
                            <StudentRegisterForm
                                initialData={studentDraft}
                                onDirty={onDirty}
                                onSubmit={(data) => {
                                    setStudentDraft(prev => ({ ...prev, ...data }));
                                    setStep(STEP.TURNS);
                                }}
                            />
                        )}

                        {/* STEP 2 — Turma e Turnos */}
                        {step === STEP.TURNS && (
                            <StudentTurnStep
                                initialGrade={turnsDraft.grade}
                                initialTurnIds={turnsDraft.turnIds}
                                onDirty={onDirty}
                                onSubmit={(data) => {
                                    setTurnsDraft(data);
                                    setStudentDraft(prev => ({ ...prev, grade: data.grade }));
                                    setStep(STEP.PARENT_SELECT);
                                }}
                            />
                        )}

                        {/* STEP 3 — Parentesco */}
                        {step === STEP.PARENT_SELECT && (
                            <div className="space-y-4">
                                {parents.map((p, i) => (
                                    <div key={i} className="border rounded p-2 text-sm flex justify-between">
                                        <span>{p.data.name}</span>
                                    </div>
                                ))}

                                {canAddMoreParents && (
                                    <StudentParentSearch
                                        onSelectParent={(parent) => addParent(parent, "EXISTING")}
                                        onCreateNewParent={() => {
                                            setEditingIndex(null);
                                            setStep(STEP.PARENT_CREATE);
                                        }}
                                        disabled={parents.length >= MAX_PARENTS}
                                    />
                                )}
                            </div>
                        )}

                        {/* STEP 3b — Criar responsável */}
                        {step === STEP.PARENT_CREATE && (
                            <ParentRegisterForm
                                onDirty={onDirty}
                                onSubmit={(parent) => addParent(parent, "NEW")}
                            />
                        )}

                        {/* STEP 4 — Resumo */}
                        {step === STEP.SUMMARY && (
                            <div className="space-y-4">
                                <h3 className="font-semibold">Resumo</h3>

                                <div className="border rounded p-4 text-sm text-start space-y-1">
                                    <div className="flex justify-between items-center">
                                        <p><strong>Aluno:</strong> {studentDraft?.name}</p>
                                        <p><strong>Série:</strong> {studentDraft?.grade || "—"}</p>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <p><strong>Email:</strong> {studentDraft?.email}</p>
                                        <p><strong>Telefone:</strong> {studentDraft?.phone_number}</p>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <p><strong>Valor de Emergência:</strong> {formatCurrency(studentDraft?.emergency_value)}</p>
                                        <p><strong>Limite de Gastos:</strong> {formatCurrency(studentDraft?.spend_limit)}</p>
                                    </div>
                                    {turnsDraft.turnIds.length > 0 && (
                                        <div className="flex justify-between items-center pt-1 border-t border-slate-100 mt-1">
                                            <p><strong>Turnos preferidos:</strong></p>
                                            <p className="text-gray-600">
                                                {turnsDraft.allTurns
                                                    .filter(t => turnsDraft.turnIds.includes(t.turnId))
                                                    .map(t => t.name)
                                                    .join(", ") || `${turnsDraft.turnIds.length} selecionado(s)`}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="border rounded p-4 space-y-3">
                                    <p className="font-medium">Responsáveis</p>

                                    {parents.map((p, index) => (
                                        <div
                                            key={index}
                                            className="flex justify-between items-center border rounded p-3 text-sm text-start"
                                        >
                                            <div>
                                                <p className="font-medium">{p.data.name}</p>
                                                <p className="text-xs text-gray-500">{p.data.email}</p>
                                                <p className="text-xs text-gray-500">{formatCPF(p.data.cpf)}</p>
                                                <p className="text-xs text-gray-500">{p.data.phone_number}</p>
                                                <p className="text-xs text-gray-500">{p.data.address}</p>
                                            </div>

                                            <CustomButton
                                                buttonType="delete"
                                                onClick={() => removeParent(index)}
                                                showIcon={true}
                                            />
                                        </div>
                                    ))}

                                    {parents.length < MAX_PARENTS && (
                                        <div className="flex items-center justify-end">
                                            <CustomButton
                                                buttonType="save"
                                                text="Adicionar"
                                                onClick={() => setStep(STEP.PARENT_SELECT)}
                                                showIcon={false}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    </motion.div>
                </AnimatePresence>
            </div>

            {/* FOOTER */}
            <div className="flex justify-between">
                <CustomButton
                    buttonType={step === STEP.STUDENT ? "cancel" : "confirmLater"}
                    text={step === STEP.STUDENT ? "Cancelar" : "Voltar"}
                    showIcon={false}
                    onClick={goBack}
                />

                {(step === STEP.STUDENT || step === STEP.TURNS || step === STEP.PARENT_CREATE) && (
                    <CustomButton buttonType="confirm" text="Avançar" showIcon={false}
                        onClick={submitActiveForm} />
                )}

                {step === STEP.PARENT_SELECT && (
                    <CustomButton
                        buttonType="confirm"
                        text="Avançar"
                        disabled={parents.length === 0}
                        showIcon={false}
                        onClick={() => setStep(STEP.SUMMARY)}
                    />
                )}

                {step === STEP.SUMMARY && (
                    <CustomButton
                        buttonType="confirm"
                        text={
                            isSubmitting ? (
                                <>
                                    <span className="ml-2">Processando</span>
                                    <Spinner />
                                </>
                            ) : (
                                "Concluir"
                            )
                        }
                        showIcon={false}
                        disabled={parents.length === 0 || isSubmitting}
                        onClick={() =>
                            onFinish({
                                student: studentDraft,
                                parents,
                                turnIds: turnsDraft.turnIds,
                            })
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default StudentRegisterStepper;
